/* Picking up a new deploy without anyone clearing the PWA cache.

   Why folio/commanddeck went stale: a Home Screen app on iOS is resumed, not
   reloaded, so the browser's own service-worker update check (which runs on
   navigation) almost never happens — and when a new worker did take over, the
   page already open kept running the old JS.

   Here:
   1. Every build has one id (vite.config.ts): it's in the bundle (__APP_BUILD__),
      in sw.js, and in /version.json.
   2. Whenever the app comes back to the foreground (and every 30 min while
      open) we ask the browser to re-check sw.js and fetch version.json
      uncached.
   3. The new worker skips waiting and claims the page (sw.js), so
      `controllerchange` fires — then we reload, unless audio is playing, in
      which case an "Update ready" pill waits until playback stops.
   4. If the old page asks for a lazy chunk the new deploy no longer has, we
      reload once instead of showing a broken screen. */
import { create } from 'zustand';
import { usePlayer } from '@/features/player/audioEngine';

export const APP_BUILD: string = typeof __APP_BUILD__ === 'string' ? __APP_BUILD__ : 'dev';

const CHECK_EVERY_MS = 30 * 60_000;
const RELOAD_GUARD = 'ftu_update_reload';

export const useUpdate = create<{ ready: boolean; checking: boolean; lastChecked: number | null }>(() => ({ ready: false, checking: false, lastChecked: null }));

const busy = () => ['playing', 'loading', 'gap'].includes(usePlayer.getState().status);

function reloadNow() {
  // One automatic reload per build — never a loop if something is genuinely broken.
  try {
    if (sessionStorage.getItem(RELOAD_GUARD) === APP_BUILD) return;
    sessionStorage.setItem(RELOAD_GUARD, APP_BUILD);
  } catch {}
  location.reload();
}

/** Reload into the new version now, or as soon as playback stops. */
function applyWhenIdle() {
  useUpdate.setState({ ready: true });
  if (!busy()) return reloadNow();
  const unsub = usePlayer.subscribe((s) => {
    if (!['playing', 'loading', 'gap'].includes(s.status)) { unsub(); reloadNow(); }
  });
}

/** Ask the browser to re-check sw.js and compare the deployed build with ours. Returns true if a newer build exists. */
export async function checkForUpdate(): Promise<boolean> {
  if (!('serviceWorker' in navigator) || !import.meta.env.PROD) return false;
  useUpdate.setState({ checking: true });
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    await reg?.update().catch(() => {});
    const r = await fetch('/version.json', { cache: 'no-store' });
    const { build } = r.ok ? ((await r.json()) as { build?: string }) : {};
    const newer = !!build && build !== APP_BUILD;
    /* Normally the new worker's controllerchange triggers the reload. If the
       worker didn't change (or the browser is slow to swap it), reload anyway —
       the shell is network-first, so the reload fetches the new index.html. */
    if (newer) setTimeout(() => { if (!useUpdate.getState().ready) applyWhenIdle(); }, 4000);
    return newer;
  } catch {
    return false;
  } finally {
    useUpdate.setState({ checking: false, lastChecked: Date.now() });
  }
}

/** "Repair app": drop the service worker and the cached shell (downloads, Qurʾān text, fonts and your data stay), then reload. */
export async function repairApp() {
  try {
    const regs = await navigator.serviceWorker?.getRegistrations();
    await Promise.all((regs ?? []).map((r) => r.unregister()));
    const keys = await caches.keys();
    await Promise.all(keys.filter((k) => k.startsWith('ftu-shell-')).map((k) => caches.delete(k)));
  } catch {}
  try { sessionStorage.removeItem(RELOAD_GUARD); } catch {}
  location.reload();
}

export function registerServiceWorker() {
  // A lazy route chunk from the previous deploy is gone → reload into the new one.
  window.addEventListener('vite:preloadError', (e) => { e.preventDefault(); reloadNow(); });
  if (!('serviceWorker' in navigator) || !import.meta.env.PROD) return;

  const hadController = !!navigator.serviceWorker.controller;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    // First install claiming the page isn't an update.
    if (hadController) applyWhenIdle();
  });

  window.addEventListener('load', () => {
    // updateViaCache 'none': the browser never serves sw.js (or its imports) from HTTP cache.
    navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' }).catch(() => {});
  });

  let last = Date.now();
  const maybeCheck = () => {
    if (document.visibilityState !== 'visible' || Date.now() - last < 60_000) return;
    last = Date.now();
    void checkForUpdate();
  };
  document.addEventListener('visibilitychange', maybeCheck);
  window.addEventListener('focus', maybeCheck);
  window.addEventListener('online', maybeCheck);
  setInterval(() => { last = 0; maybeCheck(); }, CHECK_EVERY_MS);
}
